import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageAttachment } from '@/types/api';
import { PaperclipIcon } from '@/components/icons';
import { VoiceNotePlayer } from '@/components/VoiceNotePlayer';
import { emitAppEvent } from '@/lib/appEvents';

/** Pull a MessageAttachment[] out of Message.attachments (typed `unknown`
 *  on the wire because legacy rows might be anything). */
export function parseAttachments(raw: unknown): MessageAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: MessageAttachment[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (
      typeof e.id === 'string' &&
      typeof e.name === 'string' &&
      typeof e.url === 'string' &&
      typeof e.mime === 'string' &&
      typeof e.size === 'number'
    ) {
      out.push({
        id: e.id,
        name: e.name,
        url: e.url,
        mime: e.mime,
        size: e.size,
        width: typeof e.width === 'number' ? e.width : undefined,
        height: typeof e.height === 'number' ? e.height : undefined,
        durationMs: typeof e.durationMs === 'number' ? e.durationMs : undefined,
        asFile: e.asFile === true ? true : undefined,
      });
    }
  }
  return out;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** One inline image. On load failure (expired/missing S3 object) it degrades to
 *  a labelled placeholder instead of a broken gray box with a stray timestamp. */
function ImageAttachment({
  attachment: a,
  metaOverlay,
}: {
  attachment: MessageAttachment;
  metaOverlay?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="relative flex max-w-[240px] items-center gap-2 rounded-xl border border-separator bg-surface-secondary px-3 py-2.5 text-[12px] text-ink-muted">
        <PaperclipIcon className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('messages.imageUnavailable')}</span>
        {metaOverlay}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => emitAppEvent('lightbox:open', { url: a.url, name: a.name })}
      className="relative block max-w-[360px] cursor-zoom-in overflow-hidden rounded-xl"
    >
      <img
        src={a.url}
        alt={a.name}
        loading="lazy"
        width={a.width}
        height={a.height}
        onError={() => setFailed(true)}
        className="max-h-[360px] w-auto rounded-xl object-contain"
      />
      {metaOverlay}
    </button>
  );
}

/**
 * Render every attachment on a message:
 *   - images get a constrained inline preview (max 360px wide, intrinsic
 *     aspect kept via width/height attrs to avoid layout shift)
 *   - videos get a native <video controls preload="metadata">
 *   - audio gets a native <audio controls>
 *   - everything else gets a download chip
 *
 * No lightbox yet — clicking an image opens in a new tab. Good enough for
 * MVP; Radix Dialog lightbox lives in sprint 3.
 */
export function AttachmentList({
  attachments,
  metaOverlay,
}: {
  attachments: MessageAttachment[];
  /** When set (media-only message), overlaid bottom-right on each inline image
   *  — the Telegram-style time+ticks-on-the-photo look (no bubble frame). */
  metaOverlay?: React.ReactNode;
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {attachments.map((a) => {
        if (a.mime.startsWith('image/') && !a.asFile) {
          return <ImageAttachment key={a.id} attachment={a} metaOverlay={metaOverlay} />;
        }
        if (a.mime.startsWith('video/')) {
          return (
            <video
              key={a.id}
              src={a.url}
              controls
              preload="metadata"
              className="max-h-[360px] max-w-[420px] rounded-xl bg-black"
            />
          );
        }
        if (a.mime.startsWith('audio/')) {
          // Voice notes recorded via the composer's mic button get a
          // "voice-…" filename — render those with our compact
          // Telegram-style player (play button + scrub bar + timer).
          // Other audio attachments (uploaded mp3 etc.) keep the
          // browser-default controls UI for now.
          const isVoiceNote = a.name.toLowerCase().startsWith('voice-');
          if (isVoiceNote) {
            return (
              <VoiceNotePlayer
                key={a.id}
                src={a.url}
                durationMs={a.durationMs}
              />
            );
          }
          return (
            <audio
              key={a.id}
              src={a.url}
              controls
              preload="metadata"
              className="w-full max-w-[420px]"
            />
          );
        }
        // An image sent "as file" keeps a small thumbnail in the chip so it's
        // still recognisable, but downloads (not lightbox) on click.
        const isImage = a.mime.startsWith('image/');
        return (
          <a
            key={a.id}
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            download={a.name}
            className="studio-file flex max-w-[360px] items-center gap-3 rounded-xl bg-surface-secondary p-3 text-sm transition-colors"
          >
            {isImage ? (
              <img
                src={a.url}
                alt=""
                loading="lazy"
                className="h-14 w-14 shrink-0 rounded-lg object-cover"
              />
            ) : (
              <span className="grid size-12 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent"><PaperclipIcon className="size-5" /></span>
            )}
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 break-words font-medium text-ink">{a.name}</span>
              <span className="mt-1 block text-xs text-ink-muted">{formatSize(a.size)}</span>
            </span>
          </a>
        );
      })}
    </div>
  );
}
